import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Shield, Plus, Upload, RefreshCw, BellRing, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { segurosApi } from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { PolizaFormDialog } from './seguros/PolizaFormDialog';
import { DetallePolizaDialog } from './seguros/DetallePolizaDialog';
import { CalendarioCuotas } from './seguros/CalendarioCuotas';
import { AvisoEmailDialog } from './seguros/AvisoEmailDialog';
import { MatrizPrimas } from './seguros/MatrizPrimas';
import { SegurosConfigDialog } from './seguros/SegurosConfigDialog';

const fmtCLP = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString('es-CL')}`);
const fmtUF = (v) => (v == null ? '—' : `${Number(v).toLocaleString('es-CL', { maximumFractionDigits: 4 })} UF`);

export default function SegurosPage() {
  const [polizas, setPolizas] = useState([]);
  const [ufHoy, setUfHoy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPoliza, setEditPoliza] = useState(null);
  const [detalleId, setDetalleId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showAviso, setShowAviso] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await segurosApi.listPolizas();
      setPolizas(res.data.polizas || []);
      setUfHoy(res.data.uf_hoy);
    } catch (e) {
      toast.error(`Error cargando pólizas: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await segurosApi.importarExcel(fd);
      const { creadas, errores } = res.data;
      toast.success(`✓ ${creadas} póliza(s) importada(s)${errores.length ? ` · ${errores.length} error(es)` : ''}`);
      if (errores.length) errores.slice(0, 3).forEach((er) => toast.warning(er));
      load();
    } catch (err) {
      toast.error(`Error importando: ${err.response?.data?.detail || err.message}`);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const activas = polizas.filter((p) => p.estado === 'activa');
  const totalUfPendiente = activas.reduce((s, p) => s + (p.uf_pendiente || 0), 0);

  return (
    <div className="p-6 space-y-4" data-testid="seguros-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-[#003366]" />
          <div>
            <h1 className="text-xl font-bold text-[#003366]">Seguros (UF)</h1>
            <p className="text-xs text-muted-foreground">Primas en UF, pagadas en pesos · redondeo a 0 decimales</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ufHoy && (
            <Badge variant="outline" className="text-sm px-3 py-1.5 border-emerald-300 bg-emerald-50 text-emerald-800" data-testid="uf-hoy-badge">
              UF hoy: ${Number(ufHoy.valor).toLocaleString('es-CL', { minimumFractionDigits: 2 })}
              <span className="ml-1.5 text-[10px] opacity-60">({ufHoy.fuente})</span>
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="btn-refrescar-seguros">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAviso(true)} data-testid="btn-aviso-email">
            <BellRing className="w-4 h-4 mr-1" /> Aviso Email
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} title="Cuenta contable de los egresos en Alegra" data-testid="btn-seguros-config">
            <Settings className="w-4 h-4 mr-1" /> Cuenta egresos
          </Button>
          <label>
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} data-testid="input-importar-excel" />
            <Button variant="outline" size="sm" asChild disabled={importing}>
              <span className="cursor-pointer"><Upload className="w-4 h-4 mr-1" />{importing ? 'Importando…' : 'Importar Excel'}</span>
            </Button>
          </label>
          <Button size="sm" onClick={() => { setEditPoliza(null); setShowForm(true); }} data-testid="btn-nueva-poliza">
            <Plus className="w-4 h-4 mr-1" /> Nueva Póliza
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <div className="text-2xl font-bold" data-testid="kpi-polizas-activas">{activas.length}</div>
          <div className="text-xs text-muted-foreground">Pólizas activas</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="text-2xl font-bold">{fmtUF(totalUfPendiente)}</div>
          <div className="text-xs text-muted-foreground">UF pendiente total</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="text-2xl font-bold">{ufHoy ? fmtCLP(totalUfPendiente * ufHoy.valor) : '—'}</div>
          <div className="text-xs text-muted-foreground">≈ CLP pendiente (UF hoy)</div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="matriz" className="w-full">
        <TabsList>
          <TabsTrigger value="matriz" data-testid="tab-matriz">Matriz de Primas</TabsTrigger>
          <TabsTrigger value="polizas" data-testid="tab-polizas">Pólizas y Calendario</TabsTrigger>
        </TabsList>

        <TabsContent value="matriz" className="mt-3">
          <MatrizPrimas onChanged={load} />
        </TabsContent>

        <TabsContent value="polizas" className="mt-3 space-y-4">
          <CalendarioCuotas onVerPoliza={setDetalleId} />

          <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Pólizas</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2">Póliza</th>
                  <th className="px-3 py-2">Aseguradora</th>
                  <th className="px-3 py-2">Corredora</th>
                  <th className="px-3 py-2">Bien asegurado</th>
                  <th className="px-3 py-2 text-right">UF/cuota</th>
                  <th className="px-3 py-2 text-center">Cuotas</th>
                  <th className="px-3 py-2">Próx. venc.</th>
                  <th className="px-3 py-2 text-right">CLP est.</th>
                  <th className="px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {polizas.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => setDetalleId(p.id)} data-testid={`poliza-row-${p.numero_poliza}`}>
                    <td className="px-3 py-2 font-mono font-medium">{p.numero_poliza}</td>
                    <td className="px-3 py-2">{p.aseguradora}</td>
                    <td className="px-3 py-2 text-xs">{p.corredora || '—'}</td>
                    <td className="px-3 py-2 text-xs max-w-[220px] truncate">{p.bien_asegurado}</td>
                    <td className="px-3 py-2 text-right">{fmtUF(p.uf_por_cuota)}</td>
                    <td className="px-3 py-2 text-center text-xs">{p.cuotas_pagadas}/{p.total_cuotas}</td>
                    <td className="px-3 py-2 text-xs">{p.proxima_cuota ? fmtFecha(p.proxima_cuota.fecha_vencimiento) : '—'}</td>
                    <td className="px-3 py-2 text-right font-medium">{p.proxima_cuota ? fmtCLP(p.proxima_cuota.clp_estimado) : '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant={p.estado === 'activa' ? 'default' : 'secondary'} className="text-[10px]">
                        {p.estado}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {!loading && polizas.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-muted-foreground">
                    Sin pólizas registradas. Crea una nueva o importa tu planilla Excel.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {showAviso && <AvisoEmailDialog onClose={() => setShowAviso(false)} />}
      <SegurosConfigDialog open={showConfig} onClose={() => setShowConfig(false)} />
      {showForm && (
        <PolizaFormDialog poliza={editPoliza} onClose={() => setShowForm(false)} onSaved={load} />
      )}
      {detalleId && (
        <DetallePolizaDialog
          polizaId={detalleId}
          onClose={() => setDetalleId(null)}
          onChanged={load}
          onEdit={(p) => { setDetalleId(null); setEditPoliza(p); setShowForm(true); }}
        />
      )}
    </div>
  );
}
